import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  increment,
  onSnapshot,
  type Unsubscribe,
} from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { getTenantConstraint, getTenantData } from '@/utils/tenantQuery'
import { useActivityLog } from '@/composables/useActivityLog'
import { useNotificationsStore } from '@/stores/notifications'
import type { Epic, EpicStatus, Priority } from '@/types'
import type { EpicsState } from './state'

export interface CreateEpicData {
  projectId: string
  applicationId?: string
  title: string
  description?: string
  priority: Priority
  color?: string
  startDate?: Date
  targetDate?: Date
  assignedTo?: string
  labels?: string[]
}

export interface UpdateEpicData {
  title?: string
  description?: string
  status?: EpicStatus
  priority?: Priority
  color?: string
  startDate?: Date
  targetDate?: Date
  completedAt?: Date
  assignedTo?: string
  labels?: string[]
}

export const createActions = (state: EpicsState) => {
  const { logActivity } = useActivityLog()
  const notificationsStore = useNotificationsStore()

  let globalUnsubscribe: Unsubscribe | null = null

  const subscribeGlobal = (): void => {
    if (globalUnsubscribe) return

    state.isLoading = true
    const authStore = useAuthStore()
    const tenantConstraint = getTenantConstraint()
    const baseConstraints = tenantConstraint ? [tenantConstraint] : []

    let q = query(
      collection(db, 'epics'),
      ...baseConstraints,
      orderBy('createdAt', 'desc')
    )

    if (
      authStore.user?.role === 'client' ||
      authStore.user?.role === 'member'
    ) {
      if (authStore.user.organizationId) {
        q = query(
          collection(db, 'epics'),
          ...baseConstraints,
          where(
            'organizationId',
            '==',
            authStore.user.organizationId
          ),
          orderBy('createdAt', 'desc')
        )
      }
    }

    globalUnsubscribe = onSnapshot(
      q,
      (snapshot) => {
        state.epics = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            tenantId: data.tenantId,
            ...data,
            startDate: data.startDate?.toDate?.(),
            targetDate: data.targetDate?.toDate?.(),
            completedAt: data.completedAt?.toDate?.(),
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as Epic
        })
        state.isLoading = false
      },
      (error) => {
        console.error('Error in epics subscription:', error)
        state.error = 'Error al escuchar las épicas'
        state.isLoading = false
      }
    )
  }

  const unsubscribeGlobal = (): void => {
    if (globalUnsubscribe) {
      globalUnsubscribe()
      globalUnsubscribe = null
    }
  }

  const fetchEpics = async (projectId?: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const tenantConstraint = getTenantConstraint()
      const baseConstraints = tenantConstraint ? [tenantConstraint] : []
      let q = query(collection(db, 'epics'), ...baseConstraints, orderBy('createdAt', 'desc'))

      if (projectId) {
        q = query(
          collection(db, 'epics'),
          ...baseConstraints,
          where('projectId', '==', projectId),
          orderBy('createdAt', 'desc')
        )
      } else if (authStore.user?.role === 'client' || authStore.user?.role === 'member') {
        if (authStore.user.organizationId) {
          q = query(
            collection(db, 'epics'),
            ...baseConstraints,
            where('organizationId', '==', authStore.user.organizationId),
            orderBy('createdAt', 'desc')
          )
        }
      }

      const snapshot = await getDocs(q)
      state.epics = snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          tenantId: data.tenantId,
          ...data,
          startDate: data.startDate?.toDate?.(),
          targetDate: data.targetDate?.toDate?.(),
          completedAt: data.completedAt?.toDate?.(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Epic
      })
    } catch (error) {
      console.error('Error fetching epics:', error)
      state.error = 'Error al cargar las épicas'
    } finally {
      state.isLoading = false
    }
  }

  const fetchEpicById = async (id: string): Promise<Epic | null> => {
    try {
      const docRef = doc(db, 'epics', id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        const epic = {
          id: docSnap.id,
          tenantId: data.tenantId,
          ...data,
          startDate: data.startDate?.toDate?.(),
          targetDate: data.targetDate?.toDate?.(),
          completedAt: data.completedAt?.toDate?.(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as Epic

        state.selectedEpic = epic
        return epic
      }

      return null
    } catch (error) {
      console.error('Error fetching epic:', error)
      state.error = 'Error al cargar la épica'
      return null
    }
  }

  const createEpic = async (data: CreateEpicData): Promise<string> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      // Get project to get the organization and increment epic counter
      const projectRef = doc(db, 'projects', data.projectId)
      const projectSnap = await getDoc(projectRef)

      if (!projectSnap.exists()) {
        throw new Error('Proyecto no encontrado')
      }

      const projectData = projectSnap.data()
      const epicCounter = (projectData.epicCounter || 0) + 1
      const projectKey = projectData.key || 'PROJ'

      // Update project epic counter
      await updateDoc(projectRef, {
        epicCounter: increment(1),
        updatedAt: serverTimestamp(),
      })

      const epicData: Record<string, unknown> = {
        ...getTenantData(),
        projectId: data.projectId,
        organizationId: projectData.organizationId,
        key: `${projectKey}-E${epicCounter}`,
        title: data.title,
        priority: data.priority,
        status: 'draft' as EpicStatus,
        startDate: data.startDate || null,
        targetDate: data.targetDate || null,
        labels: data.labels || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId,
        updatedBy: userId,
      }

      // Only add optional fields if they have values
      if (data.applicationId) epicData.applicationId = data.applicationId
      if (data.description) epicData.description = data.description
      if (data.color) epicData.color = data.color
      if (data.assignedTo) epicData.assignedTo = data.assignedTo

      const docRef = await addDoc(collection(db, 'epics'), epicData)

      await logActivity(
        'epic',
        docRef.id,
        'created',
        `Se creó la épica "${data.title}"`
      )

      // Notify admins + assigned user about new epic
      await notificationsStore.notifyAdminsAndStakeholders(
        [data.assignedTo].filter(Boolean) as string[],
        'epic_created',
        'Nueva épica creada',
        `Se ha creado la épica "${data.title}"`,
        'epic',
        docRef.id
      )

      await fetchEpics(data.projectId)

      return docRef.id
    } catch (error) {
      console.error('Error creating epic:', error)
      state.error = 'Error al crear la épica'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const updateEpic = async (id: string, data: UpdateEpicData): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const authStore = useAuthStore()
      const userId = authStore.user?.id || ''

      const updateData: Record<string, unknown> = {
        ...data,
        updatedAt: serverTimestamp(),
        updatedBy: userId,
      }

      // Handle status change to completed
      if (data.status === 'completed' && !data.completedAt) {
        updateData.completedAt = serverTimestamp()
      }

      await updateDoc(doc(db, 'epics', id), updateData)

      await logActivity(
        'epic',
        id,
        data.status ? 'status_changed' : 'updated',
        data.status
          ? `Se cambió el estado de la épica a "${data.status}"`
          : 'Se actualizó la épica'
      )

      // Update local state
      const index = state.epics.findIndex((e) => e.id === id)
      if (index !== -1) {
        state.epics[index] = {
          ...state.epics[index],
          ...data,
          updatedAt: new Date(),
        }
      }

      if (state.selectedEpic?.id === id) {
        state.selectedEpic = {
          ...state.selectedEpic,
          ...data,
          updatedAt: new Date(),
        }
      }
    } catch (error) {
      console.error('Error updating epic:', error)
      state.error = 'Error al actualizar la épica'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const deleteEpic = async (id: string): Promise<void> => {
    state.isLoading = true
    state.error = null

    try {
      const epic = state.epics.find((e) => e.id === id)

      await deleteDoc(doc(db, 'epics', id))

      await logActivity(
        'epic',
        id,
        'deleted',
        `Se eliminó la épica "${epic?.title || id}"`
      )

      state.epics = state.epics.filter((e) => e.id !== id)

      if (state.selectedEpic?.id === id) {
        state.selectedEpic = null
      }
    } catch (error) {
      console.error('Error deleting epic:', error)
      state.error = 'Error al eliminar la épica'
      throw error
    } finally {
      state.isLoading = false
    }
  }

  const setFilters = (filters: Partial<EpicsState['filters']>): void => {
    state.filters = { ...state.filters, ...filters }
  }

  const clearFilters = (): void => {
    state.filters = {
      search: '',
      projectId: '',
      applicationId: '',
      status: '',
      priority: '',
      assignedTo: '',
    }
  }

  const setSelectedEpic = (epic: Epic | null): void => {
    state.selectedEpic = epic
  }

  return {
    subscribeGlobal,
    unsubscribeGlobal,
    fetchEpics,
    fetchEpicById,
    createEpic,
    updateEpic,
    deleteEpic,
    setFilters,
    clearFilters,
    setSelectedEpic,
  }
}
